import { EditOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Flex,
  Input,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import type { NotificationCategorySetting, SagaCompany, SagaImportedInvoice } from '../../DTOS/FacturiScadente';

type PdfMode = 'always' | 'once';

interface InvoiceGroupRow {
  customerCui?: string;
  customerName?: string;
  totalOutstanding: number;
  totals: string;
  invoiceCount: number;
  invoices: SagaImportedInvoice[];
}

interface NotificationConfigTabProps {
  categorySettings: NotificationCategorySetting[];
  clients: SagaCompany[];
  invoiceGroups: InvoiceGroupRow[];
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  isSaving: boolean;
  onSaveChanges: (payload: {
    categories: { category: string; isEnabled: boolean }[];
    clients: {
      clientId: string;
      email?: string;
      billingEmail?: string;
      category?: string;
      isEmailNotificationsEnabled?: boolean;
      attachPaymentNotificationPdf?: boolean;
      attachPaymentNotificationPdfMode?: PdfMode;
    }[];
    invoices: {
      invoiceId: string;
      isNotificationEnabled?: boolean;
    }[];
  }) => Promise<void>;
  onOpenEditClient: (client: SagaCompany) => void;
  onRefresh: () => void;
  toNumber: (value: unknown) => number;
}

interface ConfigClientRow {
  key: string;
  client: SagaCompany;
  invoices: SagaImportedInvoice[];
  category: string;
}

interface ClientDraftSettings {
  email?: string;
  billingEmail?: string;
  category?: string;
  isEmailNotificationsEnabled?: boolean;
  attachPaymentNotificationPdf?: boolean;
  attachPaymentNotificationPdfMode?: PdfMode;
}

interface ClientStatus {
  isReady: boolean;
  color: 'green' | 'red' | 'orange' | 'default';
  reason?: string;
  activeInvoices: number;
}

interface CategoryPaginationState {
  current: number;
  pageSize: number;
}

const categoryOrder = new Map<string, number>([
  ['B2G', 1],
  ['B2B', 2],
  ['B2C', 3],
  ['fara categorie', 4],
]);

const pdfModeOptions = [
  { label: 'La fiecare trimitere', value: 'always' },
  { label: 'O singura data', value: 'once' },
];

const categoryAssignmentOptions = [
  { label: 'B2G', value: 'B2G' },
  { label: 'B2B', value: 'B2B' },
  { label: 'B2C', value: 'B2C' },
];

const normalizeCategory = (category?: string): string => {
  if (!category) {
    return 'fara categorie';
  }
  const value = category.trim().toUpperCase();
  if (value === 'B2G' || value === 'B2B' || value === 'B2C') {
    return value;
  }
  return 'fara categorie';
};

const normalizeCuiKey = (value?: string): string => (value ? value.replace(/\D/g, '') : '');

const categorySort = (left: string, right: string): number => {
  return (categoryOrder.get(left) ?? 99) - (categoryOrder.get(right) ?? 99);
};

const normalizePdfMode = (value?: string): PdfMode => {
  return value === 'once' ? 'once' : 'always';
};

const normalizeDraftEmailValue = (value?: string): string | undefined => {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
};

const NotificationConfigTab: React.FC<NotificationConfigTabProps> = ({
  categorySettings,
  clients,
  invoiceGroups,
  isLoading,
  isError,
  errorMessage,
  isSaving,
  onSaveChanges,
  onOpenEditClient,
  onRefresh,
  toNumber,
}) => {
  const { message } = App.useApp();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [activeCategory, setActiveCategory] = useState<string>('B2G');
  const [showOnlyBlocked, setShowOnlyBlocked] = useState<boolean>(false);
  const [showMissingToOnly, setShowMissingToOnly] = useState<boolean>(false);
  const [expandedClientKeys, setExpandedClientKeys] = useState<string[]>([]);
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [draftCategoryStates, setDraftCategoryStates] = useState<Map<string, boolean>>(new Map());
  const [draftClientSettings, setDraftClientSettings] = useState<Map<string, ClientDraftSettings>>(new Map());
  const [draftInvoiceStates, setDraftInvoiceStates] = useState<Map<string, boolean>>(new Map());
  const [bulkSettings, setBulkSettings] = useState<ClientDraftSettings>({});
  const [categoryPagination, setCategoryPagination] = useState<Record<string, CategoryPaginationState>>({});

  const categoryEnabledMap = useMemo(() => {
    const map = new Map<string, boolean>();
    categorySettings.forEach((setting) => {
      map.set(normalizeCategory(setting.category), !!setting.isEnabled);
    });
    return map;
  }, [categorySettings]);

  const companiesByCui = useMemo(() => {
    const map = new Map<string, SagaCompany>();
    clients.forEach((client) => {
      const key = normalizeCuiKey(client.cui);
      if (key) {
        const previous = map.get(key);
        if (!previous) {
          map.set(key, client);
          return;
        }

        const previousUpdatedAt = Date.parse(previous.modifyDate || previous.createDate || '');
        const currentUpdatedAt = Date.parse(client.modifyDate || client.createDate || '');
        const previousScore = Number.isFinite(previousUpdatedAt) ? previousUpdatedAt : -1;
        const currentScore = Number.isFinite(currentUpdatedAt) ? currentUpdatedAt : -1;

        if (
          currentScore > previousScore ||
          (currentScore === previousScore && (client.id || '') > (previous.id || ''))
        ) {
          map.set(key, client);
        }
      }
    });
    return map;
  }, [clients]);

  const configRows = useMemo(() => {
    const rows: ConfigClientRow[] = [];
    invoiceGroups.forEach((group) => {
      const key = normalizeCuiKey(group.customerCui);
      if (!key) {
        return;
      }

      const mappedCompany = companiesByCui.get(key);
      const fallbackCompany: SagaCompany = {
        id: '',
        cui: group.customerCui || key,
        name: group.customerName || `Client ${key}`,
        category: 'fara categorie',
        isEmailNotificationsEnabled: false,
        attachPaymentNotificationPdf: false,
        attachPaymentNotificationPdfMode: 'always',
      };
      const client = mappedCompany ?? fallbackCompany;
      const category = normalizeCategory(client.category);

      rows.push({
        key: client.id || key,
        client: { ...client, category },
        invoices: group.invoices ?? [],
        category,
      });
    });

    return rows.sort((left, right) => {
      const nameLeft = left.client.name || left.client.cui || left.key;
      const nameRight = right.client.name || right.client.cui || right.key;
      return nameLeft.localeCompare(nameRight, 'ro', { sensitivity: 'base' });
    });
  }, [invoiceGroups, companiesByCui]);

  const rowsByCategory = useMemo(() => {
    const grouped = new Map<string, ConfigClientRow[]>();
    configRows.forEach((row) => {
      const list = grouped.get(row.category) ?? [];
      list.push(row);
      grouped.set(row.category, list);
    });
    return grouped;
  }, [configRows]);

  const allCategories = useMemo(() => {
    const fromSettings = categorySettings.map((setting) => normalizeCategory(setting.category));
    const fromRows = Array.from(rowsByCategory.keys());
    const unique = Array.from(new Set([...fromSettings, ...fromRows]));
    return unique.sort(categorySort);
  }, [categorySettings, rowsByCategory]);

  const visibleCategories = useMemo(
    () =>
      allCategories.filter((category) =>
        draftCategoryStates.has(category)
          ? (draftCategoryStates.get(category) ?? false)
          : (categoryEnabledMap.get(category) ?? false),
      ),
    [allCategories, draftCategoryStates, categoryEnabledMap],
  );

  useEffect(() => {
    if (visibleCategories.length === 0) {
      return;
    }
    if (!visibleCategories.includes(activeCategory)) {
      setActiveCategory(visibleCategories[0]);
    }
  }, [activeCategory, visibleCategories]);

  useEffect(() => {
    const validKeys = new Set(configRows.map((row) => row.key));
    const validClientIds = new Set(configRows.map((row) => row.client.id).filter((id): id is string => !!id));
    setExpandedClientKeys((previous) => previous.filter((key) => validKeys.has(key)));
    setSelectedClientIds((previous) => previous.filter((id) => validClientIds.has(id)));
  }, [configRows]);

  const effectiveCategoryEnabled = (category: string): boolean => {
    if (draftCategoryStates.has(category)) {
      return draftCategoryStates.get(category) ?? false;
    }
    return categoryEnabledMap.get(category) ?? false;
  };

  const effectiveClientField = <K extends keyof ClientDraftSettings>(
    client: SagaCompany,
    field: K,
  ): ClientDraftSettings[K] => {
    if (client.id && draftClientSettings.has(client.id)) {
      const draftValue = draftClientSettings.get(client.id)?.[field];
      if (draftValue !== undefined) {
        return draftValue;
      }
    }

    switch (field) {
      case 'email':
        return normalizeDraftEmailValue(client.email) as ClientDraftSettings[K];
      case 'billingEmail':
        return normalizeDraftEmailValue(client.billingEmail) as ClientDraftSettings[K];
      case 'category':
        return normalizeCategory(client.category) as ClientDraftSettings[K];
      case 'isEmailNotificationsEnabled':
        return (client.isEmailNotificationsEnabled !== false) as ClientDraftSettings[K];
      case 'attachPaymentNotificationPdf':
        return (client.attachPaymentNotificationPdf === true) as ClientDraftSettings[K];
      case 'attachPaymentNotificationPdfMode':
        return normalizePdfMode(client.attachPaymentNotificationPdfMode) as ClientDraftSettings[K];
      default:
        return undefined as ClientDraftSettings[K];
    }
  };

  const setClientDraftField = <K extends keyof ClientDraftSettings>(
    client: SagaCompany,
    field: K,
    value: ClientDraftSettings[K],
  ) => {
    if (!client.id) {
      return;
    }

    const normalizedValue = (
      field === 'email' || field === 'billingEmail'
        ? normalizeDraftEmailValue(value as string | undefined)
        : field === 'category'
          ? normalizeCategory(value as string | undefined)
          : value
    ) as ClientDraftSettings[K];

    const baseValue = (() => {
      switch (field) {
        case 'email':
          return normalizeDraftEmailValue(client.email);
        case 'billingEmail':
          return normalizeDraftEmailValue(client.billingEmail);
        case 'category':
          return normalizeCategory(client.category);
        case 'isEmailNotificationsEnabled':
          return client.isEmailNotificationsEnabled !== false;
        case 'attachPaymentNotificationPdf':
          return client.attachPaymentNotificationPdf === true;
        case 'attachPaymentNotificationPdfMode':
          return normalizePdfMode(client.attachPaymentNotificationPdfMode);
        default:
          return undefined;
      }
    })();

    setDraftClientSettings((previous) => {
      const next = new Map(previous);
      const existing = { ...(next.get(client.id) ?? {}) };
      const isEqual = normalizedValue === (baseValue as ClientDraftSettings[K]);

      if (isEqual) {
        delete existing[field];
      } else {
        existing[field] = normalizedValue;
      }

      if (Object.keys(existing).length === 0) {
        next.delete(client.id);
      } else {
        next.set(client.id, existing);
      }
      return next;
    });
  };

  const effectiveInvoiceEnabled = (invoice: SagaImportedInvoice): boolean => {
    if (draftInvoiceStates.has(invoice.id)) {
      return draftInvoiceStates.get(invoice.id) ?? false;
    }
    return invoice.isNotificationEnabled !== false;
  };

  const isUpcomingByDate = (invoice: SagaImportedInvoice): boolean => {
    if (!invoice.dueDate) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(invoice.dueDate);
    due.setHours(0, 0, 0, 0);
    return due > today;
  };

  const setInvoiceDraft = (invoice: SagaImportedInvoice, checked: boolean) => {
    setDraftInvoiceStates((previous) => {
      const next = new Map(previous);
      const baseValue = invoice.isNotificationEnabled !== false;
      if (checked === baseValue) {
        next.delete(invoice.id);
      } else {
        next.set(invoice.id, checked);
      }
      return next;
    });
  };

  const setAllInvoicesForRow = (row: ConfigClientRow, checked: boolean) => {
    setDraftInvoiceStates((previous) => {
      const next = new Map(previous);
      row.invoices.forEach((invoice) => {
        const baseValue = invoice.isNotificationEnabled !== false;
        if (checked === baseValue) {
          next.delete(invoice.id);
        } else {
          next.set(invoice.id, checked);
        }
      });
      return next;
    });
  };

  const getClientStatus = (row: ConfigClientRow): ClientStatus => {
    const categoryEnabled = effectiveCategoryEnabled(row.category);
    const clientEnabled = effectiveClientField(row.client, 'isEmailNotificationsEnabled') === true;
    const toEmail = effectiveClientField(row.client, 'email');
    const hasTo = !!toEmail?.trim();
    const activeInvoices = row.invoices.filter((invoice) => effectiveInvoiceEnabled(invoice)).length;

    if (!categoryEnabled) {
      return {
        isReady: false,
        color: 'default',
        reason: 'Categoria este oprita',
        activeInvoices,
      };
    }
    if (!clientEnabled) {
      return {
        isReady: false,
        color: 'orange',
        reason: 'Clientul are notificari oprite',
        activeInvoices,
      };
    }
    if (!hasTo) {
      return {
        isReady: false,
        color: 'red',
        reason: 'Lipseste email To',
        activeInvoices,
      };
    }
    if (activeInvoices <= 0) {
      return {
        isReady: false,
        color: 'orange',
        reason: 'Nu exista facturi selectate',
        activeInvoices,
      };
    }

    return {
      isReady: true,
      color: 'green',
      reason: 'Poate fi trimis',
      activeInvoices,
    };
  };

  const hasDraftChanges = draftCategoryStates.size > 0 || draftClientSettings.size > 0 || draftInvoiceStates.size > 0;

  const hasBulkChanges =
    normalizeDraftEmailValue(bulkSettings.email) !== undefined ||
    normalizeDraftEmailValue(bulkSettings.billingEmail) !== undefined ||
    bulkSettings.category !== undefined ||
    bulkSettings.isEmailNotificationsEnabled !== undefined ||
    bulkSettings.attachPaymentNotificationPdf !== undefined ||
    bulkSettings.attachPaymentNotificationPdfMode !== undefined;

  const resetDraft = () => {
    setDraftCategoryStates(new Map());
    setDraftClientSettings(new Map());
    setDraftInvoiceStates(new Map());
  };

  const resetBulkSettings = () => {
    setBulkSettings({});
  };

  const handleSave = async () => {
    if (!hasDraftChanges) {
      message.info('Nu exista modificari de salvat.');
      return;
    }

    const categories = Array.from(draftCategoryStates.entries()).map(([category, isEnabled]) => ({
      category,
      isEnabled,
    }));

    const clientsPayload = Array.from(draftClientSettings.entries())
      .map(([clientId, settings]) => {
        const payload: {
          clientId: string;
          email?: string;
          billingEmail?: string;
          category?: string;
          isEmailNotificationsEnabled?: boolean;
          attachPaymentNotificationPdf?: boolean;
          attachPaymentNotificationPdfMode?: PdfMode;
        } = { clientId };

        if (settings.email !== undefined) {
          payload.email = settings.email;
        }
        if (settings.billingEmail !== undefined) {
          payload.billingEmail = settings.billingEmail;
        }
        if (settings.category !== undefined) {
          payload.category = settings.category;
        }
        if (settings.isEmailNotificationsEnabled !== undefined) {
          payload.isEmailNotificationsEnabled = settings.isEmailNotificationsEnabled;
        }
        if (settings.attachPaymentNotificationPdf !== undefined) {
          payload.attachPaymentNotificationPdf = settings.attachPaymentNotificationPdf;
        }
        if (settings.attachPaymentNotificationPdfMode !== undefined) {
          payload.attachPaymentNotificationPdfMode = settings.attachPaymentNotificationPdfMode;
        }

        return payload;
      })
      .filter((item) => Object.keys(item).length > 1);

    const invoices = Array.from(draftInvoiceStates.entries()).map(([invoiceId, isNotificationEnabled]) => ({
      invoiceId,
      isNotificationEnabled,
    }));

    await onSaveChanges({
      categories,
      clients: clientsPayload,
      invoices,
    });

    resetDraft();
  };

  const getFilteredRowsForCategory = (category: string): ConfigClientRow[] => {
    return (rowsByCategory.get(category) ?? []).filter((row) => {
      if (searchTerm.trim()) {
        const value = searchTerm.trim().toLowerCase();
        const match =
          (row.client.name || '').toLowerCase().includes(value) || (row.client.cui || '').toLowerCase().includes(value);
        if (!match) {
          return false;
        }
      }

      if (showMissingToOnly) {
        const toEmail = effectiveClientField(row.client, 'email');
        if (!!toEmail?.trim()) {
          return false;
        }
      }

      if (showOnlyBlocked) {
        const status = getClientStatus(row);
        if (status.isReady) {
          return false;
        }
      }

      return true;
    });
  };

  const getCategoryPagination = (category: string, totalRows: number): CategoryPaginationState => {
    const fallback: CategoryPaginationState = { current: 1, pageSize: 20 };
    const saved = categoryPagination[category] ?? fallback;
    const safePageSize = saved.pageSize > 0 ? saved.pageSize : fallback.pageSize;
    const maxPage = Math.max(1, Math.ceil(totalRows / safePageSize));
    const safeCurrent = Math.min(Math.max(1, saved.current), maxPage);

    return {
      current: safeCurrent,
      pageSize: safePageSize,
    };
  };

  const updateCategoryPagination = (category: string, current: number, pageSize: number) => {
    const safePageSize = pageSize > 0 ? pageSize : 20;
    const safeCurrent = current > 0 ? current : 1;
    setCategoryPagination((previous) => ({
      ...previous,
      [category]: {
        current: safeCurrent,
        pageSize: safePageSize,
      },
    }));
  };

  const expandAllVisibleRows = () => {
    const keys = visibleCategories.flatMap((category) => getFilteredRowsForCategory(category).map((row) => row.key));
    setExpandedClientKeys(Array.from(new Set(keys)));
  };

  const collapseAllRows = () => {
    setExpandedClientKeys([]);
  };

  const applyBulkSettings = (rows: ConfigClientRow[]) => {
    const selectedRows = rows.filter((row) => !!row.client.id && selectedClientIds.includes(row.client.id));
    if (selectedRows.length === 0) {
      message.warning('Selecteaza cel putin un client.');
      return;
    }
    if (!hasBulkChanges) {
      message.warning('Seteaza cel putin un camp de configurare in bloc.');
      return;
    }

    selectedRows.forEach((row) => {
      const bulkTo = normalizeDraftEmailValue(bulkSettings.email);
      if (bulkTo !== undefined) {
        setClientDraftField(row.client, 'email', bulkTo);
      }
      const bulkBcc = normalizeDraftEmailValue(bulkSettings.billingEmail);
      if (bulkBcc !== undefined) {
        setClientDraftField(row.client, 'billingEmail', bulkBcc);
      }
      if (bulkSettings.category !== undefined && row.category === 'fara categorie') {
        setClientDraftField(row.client, 'category', bulkSettings.category);
      }
      if (bulkSettings.isEmailNotificationsEnabled !== undefined) {
        setClientDraftField(row.client, 'isEmailNotificationsEnabled', bulkSettings.isEmailNotificationsEnabled);
        if (!bulkSettings.isEmailNotificationsEnabled) {
          setAllInvoicesForRow(row, false);
        }
      }
      if (bulkSettings.attachPaymentNotificationPdf !== undefined) {
        setClientDraftField(row.client, 'attachPaymentNotificationPdf', bulkSettings.attachPaymentNotificationPdf);
      }
      if (bulkSettings.attachPaymentNotificationPdfMode !== undefined) {
        setClientDraftField(
          row.client,
          'attachPaymentNotificationPdfMode',
          bulkSettings.attachPaymentNotificationPdfMode,
        );
      }
    });

    message.success(`Setari aplicate pentru ${selectedRows.length} clienti.`);
  };

  if (isLoading) {
    return <Spin size='large' className='spinner' />;
  }

  if (isError) {
    return <Alert type='error' message={errorMessage || 'Eroare la incarcare configurare notificari.'} />;
  }

  return (
    <div className='table-page-no-h-scroll'>
      <Alert
        type='info'
        showIcon
        style={{ marginBottom: 12 }}
        message='Logica de trimitere este pe 3 niveluri'
        description={
          <div>
            <div>1) Categoria trebuie sa fie ON.</div>
            <div>2) Clientul trebuie sa fie ON si sa aiba email To.</div>
            <div>3) Trebuie cel putin o factura marcata ON.</div>
          </div>
        }
      />

      <Card
        size='small'
        title='Nivel 1 - categorii'
        extra={
          <Space>
            <Tag color={hasDraftChanges ? 'orange' : 'green'}>
              Draft: {hasDraftChanges ? 'modificari nesalvate' : 'fara modificari'}
            </Tag>
            <Button size='small' onClick={resetDraft} disabled={!hasDraftChanges || isSaving}>
              Reseteaza draft
            </Button>
            <Button
              type='primary'
              size='small'
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={isSaving}
              disabled={!hasDraftChanges}
            >
              Salveaza toate modificarile
            </Button>
            <Button size='small' icon={<ReloadOutlined />} onClick={onRefresh} disabled={isSaving}>
              Reincarca
            </Button>
          </Space>
        }
        style={{ marginBottom: 12 }}
      >
        <Space wrap>
          {allCategories.map((category) => (
            <Space
              key={category}
              style={{ padding: '6px 10px', border: '1px solid #f0f0f0', borderRadius: 8, background: '#fafafa' }}
            >
              <Typography.Text>{category}</Typography.Text>
              <Switch
                size='small'
                checked={effectiveCategoryEnabled(category)}
                disabled={isSaving}
                onChange={(checked) => {
                  setDraftCategoryStates((previous) => {
                    const next = new Map(previous);
                    const baseValue = categoryEnabledMap.get(category) ?? false;
                    if (checked === baseValue) {
                      next.delete(category);
                    } else {
                      next.set(category, checked);
                    }
                    return next;
                  });
                }}
              />
            </Space>
          ))}
        </Space>
      </Card>

      <Card
        size='small'
        title='Nivel 2 + 3 - clienti si facturi'
        extra={
          <Flex gap={8} wrap='wrap' align='center'>
            <Input
              allowClear
              placeholder='Cauta client / CUI'
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              style={{ width: 240 }}
            />
            <Checkbox checked={showOnlyBlocked} onChange={(e) => setShowOnlyBlocked(e.target.checked)}>
              Doar clienti blocati
            </Checkbox>
            <Checkbox checked={showMissingToOnly} onChange={(e) => setShowMissingToOnly(e.target.checked)}>
              Doar fara To
            </Checkbox>
            <Button size='small' onClick={expandAllVisibleRows} disabled={visibleCategories.length === 0}>
              Extinde tot
            </Button>
            <Button size='small' onClick={collapseAllRows} disabled={expandedClientKeys.length === 0}>
              Colapseaza tot
            </Button>
          </Flex>
        }
      >
        {visibleCategories.length === 0 ? (
          <Alert
            type='warning'
            showIcon
            message='Nu exista categorii active.'
            description='Activeaza cel putin o categorie la Nivel 1 pentru a configura clienti si facturi.'
          />
        ) : (
          <Tabs
            activeKey={activeCategory}
            onChange={setActiveCategory}
            items={visibleCategories.map((category) => {
              const categoryRows = getFilteredRowsForCategory(category);
              const paginationState = getCategoryPagination(category, categoryRows.length);
              const categoryEnabled = effectiveCategoryEnabled(category);
              const readyCount = categoryRows.filter((row) => getClientStatus(row).isReady).length;
              const selectableIdsInCategory = categoryRows
                .map((row) => row.client.id)
                .filter((id): id is string => !!id);
              const selectedInCategory = categoryRows.filter(
                (row) => !!row.client.id && selectedClientIds.includes(row.client.id),
              ).length;

              const columns = [
                {
                  title: 'Client',
                  key: 'client',
                  width: 260,
                  render: (_: unknown, row: ConfigClientRow) => (
                    <div>
                      <div style={{ fontWeight: 600 }}>{row.client.name || '-'}</div>
                      <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>{row.client.cui}</div>
                    </div>
                  ),
                },
                {
                  title: 'To',
                  key: 'toEmail',
                  width: 220,
                  render: (_: unknown, row: ConfigClientRow) => {
                    const toEmail = effectiveClientField(row.client, 'email');
                    return toEmail?.trim() ? (
                      <Tooltip title={toEmail}>
                        <span>{toEmail}</span>
                      </Tooltip>
                    ) : (
                      <Tag color='red'>Lipseste</Tag>
                    );
                  },
                },
                {
                  title: 'BCC',
                  key: 'bccEmail',
                  width: 220,
                  render: (_: unknown, row: ConfigClientRow) => {
                    const bccEmail = effectiveClientField(row.client, 'billingEmail');
                    return bccEmail?.trim() ? (
                      <Tooltip title={bccEmail}>
                        <span>{bccEmail}</span>
                      </Tooltip>
                    ) : (
                      <span>-</span>
                    );
                  },
                },
                {
                  title: 'Client ON',
                  key: 'clientEnabled',
                  width: 120,
                  render: (_: unknown, row: ConfigClientRow) => (
                    <Switch
                      size='small'
                      checked={effectiveClientField(row.client, 'isEmailNotificationsEnabled') === true}
                      disabled={!row.client.id || isSaving}
                      onChange={(checked) => {
                        setClientDraftField(row.client, 'isEmailNotificationsEnabled', checked);
                        if (!checked) {
                          setAllInvoicesForRow(row, false);
                        }
                      }}
                    />
                  ),
                },
                {
                  title: 'Categorie',
                  key: 'category',
                  width: 180,
                  render: (_: unknown, row: ConfigClientRow) => {
                    const category = normalizeCategory(
                      effectiveClientField(row.client, 'category') as string | undefined,
                    );
                    const isClientEnabled = effectiveClientField(row.client, 'isEmailNotificationsEnabled') === true;
                    const isOriginallyUncategorized = normalizeCategory(row.client.category) === 'fara categorie';
                    const canSetCategory = isOriginallyUncategorized && isClientEnabled;

                    if (canSetCategory) {
                      return (
                        <Select
                          size='small'
                          value={category === 'fara categorie' ? undefined : category}
                          style={{ width: 160 }}
                          placeholder='Seteaza categorie'
                          options={categoryAssignmentOptions}
                          disabled={!row.client.id || isSaving}
                          onChange={(value) => setClientDraftField(row.client, 'category', value)}
                        />
                      );
                    }

                    if (category === 'fara categorie' && !isClientEnabled) {
                      return <Tag>Fara categorie</Tag>;
                    }

                    return <Tag color='blue'>{category}</Tag>;
                  },
                },
                {
                  title: 'PDF',
                  key: 'attachPdf',
                  width: 90,
                  render: (_: unknown, row: ConfigClientRow) => (
                    <Switch
                      size='small'
                      checked={effectiveClientField(row.client, 'attachPaymentNotificationPdf') === true}
                      disabled={!row.client.id || isSaving}
                      onChange={(checked) => setClientDraftField(row.client, 'attachPaymentNotificationPdf', checked)}
                    />
                  ),
                },
                {
                  title: 'Mod PDF',
                  key: 'pdfMode',
                  width: 150,
                  render: (_: unknown, row: ConfigClientRow) => (
                    <Select
                      size='small'
                      value={effectiveClientField(row.client, 'attachPaymentNotificationPdfMode')}
                      style={{ width: 140 }}
                      options={pdfModeOptions}
                      disabled={!row.client.id || isSaving}
                      onChange={(value) => setClientDraftField(row.client, 'attachPaymentNotificationPdfMode', value)}
                    />
                  ),
                },
                {
                  title: 'Facturi ON',
                  key: 'invoicesOn',
                  width: 120,
                  render: (_: unknown, row: ConfigClientRow) => {
                    const status = getClientStatus(row);
                    return `${status.activeInvoices}/${row.invoices.length}`;
                  },
                },
                {
                  title: 'Status',
                  key: 'status',
                  width: 180,
                  render: (_: unknown, row: ConfigClientRow) => {
                    const status = getClientStatus(row);
                    return (
                      <Tooltip title={status.reason}>
                        <Tag color={status.color}>{status.isReady ? 'Ready' : 'Blocat'}</Tag>
                      </Tooltip>
                    );
                  },
                },
                {
                  title: 'Actiuni',
                  key: 'actions',
                  width: 110,
                  render: (_: unknown, row: ConfigClientRow) => (
                    <Button
                      size='small'
                      icon={<EditOutlined />}
                      disabled={!row.client.id}
                      onClick={() => onOpenEditClient(row.client)}
                    >
                      Edit
                    </Button>
                  ),
                },
              ];

              return {
                key: category,
                label: (
                  <Space size={6}>
                    <span>{category}</span>
                    <Tag>{categoryRows.length}</Tag>
                    <Tag color={categoryEnabled ? 'green' : 'red'}>{categoryEnabled ? 'ON' : 'OFF'}</Tag>
                    <Tag color='blue'>Ready: {readyCount}</Tag>
                  </Space>
                ),
                children:
                  categoryRows.length === 0 ? (
                    <Alert type='info' showIcon message='Nu exista clienti in aceasta categorie cu filtrele curente.' />
                  ) : (
                    <>
                      <Card size='small' style={{ marginBottom: 12 }}>
                        <Space wrap>
                          <Tag>Selectati in tab: {selectedInCategory}</Tag>
                          <Button
                            size='small'
                            disabled={selectedInCategory === 0}
                            onClick={() =>
                              setSelectedClientIds((previous) =>
                                previous.filter((id) => !selectableIdsInCategory.includes(id)),
                              )
                            }
                          >
                            Deselecteaza in tab
                          </Button>
                          <Select
                            allowClear
                            placeholder='Client ON/OFF'
                            style={{ width: 150 }}
                            value={bulkSettings.isEmailNotificationsEnabled}
                            options={[
                              { label: 'Client ON', value: true },
                              { label: 'Client OFF', value: false },
                            ]}
                            onChange={(value) =>
                              setBulkSettings((previous) => ({ ...previous, isEmailNotificationsEnabled: value }))
                            }
                          />
                          <Select
                            allowClear
                            placeholder='PDF ON/OFF'
                            style={{ width: 140 }}
                            value={bulkSettings.attachPaymentNotificationPdf}
                            options={[
                              { label: 'PDF ON', value: true },
                              { label: 'PDF OFF', value: false },
                            ]}
                            onChange={(value) =>
                              setBulkSettings((previous) => ({ ...previous, attachPaymentNotificationPdf: value }))
                            }
                          />
                          <Select
                            allowClear
                            placeholder='Mod PDF'
                            style={{ width: 170 }}
                            value={bulkSettings.attachPaymentNotificationPdfMode}
                            options={pdfModeOptions}
                            onChange={(value) =>
                              setBulkSettings((previous) => ({ ...previous, attachPaymentNotificationPdfMode: value }))
                            }
                          />
                          <Input
                            allowClear
                            placeholder='To in bloc (email1@domeniu.ro, email2@domeniu.ro)'
                            style={{ width: 320 }}
                            value={bulkSettings.email}
                            onChange={(event) =>
                              setBulkSettings((previous) => ({ ...previous, email: event.target.value }))
                            }
                          />
                          <Input
                            allowClear
                            placeholder='Bcc in bloc (email1@domeniu.ro, email2@domeniu.ro)'
                            style={{ width: 320 }}
                            value={bulkSettings.billingEmail}
                            onChange={(event) =>
                              setBulkSettings((previous) => ({ ...previous, billingEmail: event.target.value }))
                            }
                          />{' '}
                          {category === 'fara categorie' && (
                            <Select
                              allowClear
                              placeholder='Categorie in bloc'
                              style={{ width: 170 }}
                              value={bulkSettings.category}
                              options={categoryAssignmentOptions}
                              onChange={(value) => setBulkSettings((previous) => ({ ...previous, category: value }))}
                            />
                          )}
                          <Button onClick={resetBulkSettings}>Curata setari bloc</Button>
                          <Button type='primary' onClick={() => applyBulkSettings(categoryRows)}>
                            Aplica pe clientii selectati
                          </Button>
                        </Space>
                      </Card>

                      <Table<ConfigClientRow>
                        rowKey={(row) => row.key}
                        dataSource={categoryRows}
                        columns={columns}
                        scroll={{ x: true }}
                        pagination={{
                          current: paginationState.current,
                          pageSize: paginationState.pageSize,
                          total: categoryRows.length,
                          showSizeChanger: true,
                          onChange: (current, pageSize) =>
                            updateCategoryPagination(category, current, pageSize || paginationState.pageSize),
                        }}
                        rowSelection={{
                          selectedRowKeys: selectedClientIds,
                          getCheckboxProps: (row) => ({
                            disabled: !row.client.id,
                          }),
                          onSelect: (row, selected) => {
                            if (!row.client.id) {
                              return;
                            }
                            setSelectedClientIds((previous) => {
                              if (selected) {
                                return previous.includes(row.client.id) ? previous : [...previous, row.client.id];
                              }
                              return previous.filter((id) => id !== row.client.id);
                            });
                          },
                          onSelectAll: (selected, _selectedRows, changedRows) => {
                            const changedIds = changedRows
                              .map((row) => row.client.id)
                              .filter((id): id is string => !!id);
                            setSelectedClientIds((previous) => {
                              if (selected) {
                                return Array.from(new Set([...previous, ...changedIds]));
                              }
                              return previous.filter((id) => !changedIds.includes(id));
                            });
                          },
                        }}
                        expandable={{
                          expandedRowKeys: expandedClientKeys,
                          onExpandedRowsChange: (keys) => setExpandedClientKeys(keys.map(String)),
                          expandedRowRender: (row) => {
                            const invoiceColumns = [
                              {
                                title: 'Factura',
                                dataIndex: 'invoiceNumber',
                                key: 'invoiceNumber',
                                render: (value: string, record: SagaImportedInvoice) =>
                                  value || record.invoiceSeries || '-',
                              },
                              {
                                title: 'Tip',
                                key: 'invoiceType',
                                width: 150,
                                render: (_: unknown, invoice: SagaImportedInvoice) =>
                                  isUpcomingByDate(invoice) ? (
                                    <Tag color='blue'>Urmeaza</Tag>
                                  ) : (
                                    <Tag color='orange'>Scadenta/Restanta</Tag>
                                  ),
                              },
                              {
                                title: 'Scadenta',
                                dataIndex: 'dueDate',
                                key: 'dueDate',
                                render: (value: string) => (value ? new Date(value).toLocaleDateString('ro-RO') : '-'),
                              },
                              {
                                title: 'Restant',
                                dataIndex: 'outstanding',
                                key: 'outstanding',
                                render: (value: number) => toNumber(value).toLocaleString('ro-RO'),
                              },
                              {
                                title: 'Notificare',
                                dataIndex: 'isNotificationEnabled',
                                key: 'isNotificationEnabled',
                                width: 130,
                                render: (_: boolean, invoice: SagaImportedInvoice) => (
                                  <Switch
                                    size='small'
                                    checked={effectiveInvoiceEnabled(invoice)}
                                    disabled={
                                      isSaving ||
                                      effectiveClientField(row.client, 'isEmailNotificationsEnabled') !== true
                                    }
                                    onChange={(checked) => setInvoiceDraft(invoice, checked)}
                                  />
                                ),
                              },
                            ];

                            return (
                              <div>
                                <Space style={{ marginBottom: 10 }} wrap>
                                  <Tag>To: {effectiveClientField(row.client, 'email') || '-'}</Tag>
                                  <Tag>BCC: {effectiveClientField(row.client, 'billingEmail') || '-'}</Tag>
                                  <Tag>
                                    Facturi ON: {getClientStatus(row).activeInvoices}/{row.invoices.length}
                                  </Tag>
                                  <Button
                                    size='small'
                                    disabled={effectiveClientField(row.client, 'isEmailNotificationsEnabled') !== true}
                                    onClick={() => setAllInvoicesForRow(row, true)}
                                  >
                                    Toate ON
                                  </Button>
                                  <Button
                                    size='small'
                                    disabled={effectiveClientField(row.client, 'isEmailNotificationsEnabled') !== true}
                                    onClick={() => setAllInvoicesForRow(row, false)}
                                  >
                                    Toate OFF
                                  </Button>
                                </Space>

                                <Table<SagaImportedInvoice>
                                  rowKey='id'
                                  columns={invoiceColumns}
                                  dataSource={row.invoices}
                                  pagination={false}
                                  size='small'
                                  scroll={{ x: true }}
                                />
                              </div>
                            );
                          },
                        }}
                      />
                    </>
                  ),
              };
            })}
          />
        )}
      </Card>
    </div>
  );
};

export default NotificationConfigTab;
